
import { Component, Vue, Prop } from 'vue-property-decorator';
import { ApplicationDocumentRequest, FeatureFlagging } from '@sageworks/jpi/';
import { OnlinePortalDocumentRequest, OnlinePortalApplicationDocumentRequest, IDocumentRequest } from '@/models';
import ConfirmReasonModal from '../../documents/confirm-reason-modal/confirm-reason-modal.vue';
import MarkOnFileBadge from '../../documents/mark-on-file-badge/mark-on-file-badge.vue';
import { DateFormatting } from '@sageworks/core-logic';

@Component({
	components: { ConfirmReasonModal, MarkOnFileBadge }
})
export default class DocumentDetailsLenderFeatures extends Vue {
	@Prop()
	public isApplicationDocumentRequest!: boolean;
	@Prop()
	public isOpenRequest!: boolean;
	@Prop()
	public documentRequest!: IDocumentRequest;
	@Prop()
	public customerFacingName!: string;
	@Prop()
	public isAdditionalDocument!: boolean;
	@Prop({ default: 0 })
	public potentialMatchCount!: number;

	public waiveInProgress: boolean = false;
	public reopenInProgress: boolean = false;
	public deleteInProgress: boolean = false;

	get disableFileInput(): boolean {
		return (
			this.isApplicationDocumentRequest &&
			(!!this.documentRequest.dateUploaded || this.reopenInProgress || this.waiveInProgress || !!this.documentRequest.dateWaived)
		);
	}

	get documentHasBeenUploaded(): boolean {
		return !!this.documentRequest.documentId && this.documentRequest.documentId > 0;
	}

	get waiveModalId() {
		return `waiveModal${this.documentRequest.id}`;
	}

	get shouldShowMarkOnFileBadge(): boolean {
		// Only show for core document requests when there are potential matches and the document hasn't been uploaded or waived
		const hasMatches = this.potentialMatchCount > 0 && this.isApplicationDocumentRequest &&
			!this.documentRequest.dateUploaded && !this.documentRequest.dateWaived;
		const isFeatureEnabled = this.directStore.state.FeatureFlag.activeFeatures?.has(
			FeatureFlagging.FeatureFlagEnum.EnableOpnDocumentDetection
		) ?? false;
		return hasMatches && isFeatureEnabled;
	}

	get statusMessageForDocumentRequest(): string {
		let docStatusMessage = 'Status: New';

		if (this.documentRequest.dateUploaded) {
			docStatusMessage = `Uploaded ${this.documentRequest.dateUploaded}`;
		} else if (this.documentRequest.dateWaived && (this.documentRequest as OnlinePortalApplicationDocumentRequest).waivedReason === 'Document on file') {
			docStatusMessage = `Marked on file ${this.documentRequest.dateWaived}`;
		} else if (this.documentRequest.dateWaived) {
			docStatusMessage = `Waived ${this.documentRequest.dateWaived} for reason: ${
				(this.documentRequest as OnlinePortalApplicationDocumentRequest).waivedReason
			}`;
		}

		return docStatusMessage;
	}

	public async unWaiveDocumentRequest(): Promise<void> {
		try {
			if (this.isApplicationDocumentRequest) {
				(this.documentRequest as ApplicationDocumentRequest).waivedByLoanOfficerId = this.directStore.state.User.proxyUser?.id;
				(this.documentRequest as ApplicationDocumentRequest).dateWaived = undefined;
				(this.documentRequest as ApplicationDocumentRequest).waivedReason = undefined;
				await this.directStore.dispatch.ApplicationDocumentRequest.updateApplicationDocumentRequest(
					this.documentRequest as OnlinePortalApplicationDocumentRequest
				);
			} else {
				(this.documentRequest as OnlinePortalDocumentRequest).request.waivedByLoanOfficerId = this.directStore.state.User.proxyUser?.id;
				(this.documentRequest as OnlinePortalDocumentRequest).request.dateWaived = undefined;
				(this.documentRequest as OnlinePortalDocumentRequest).request.waivedReason = undefined;
				await this.directStore.dispatch.DocumentRequest.updateDocumentRequest(this.documentRequest as OnlinePortalDocumentRequest);
				await this.directStore.dispatch.DocumentRequest.loadPagedDocumentRequests({ page: 1, perPage: 20, refreshCache: true });
			}
			return Promise.resolve();
		} catch (err) {
			return Promise.reject(err);
		}
	}

	public async waiveDocumentRequest({ reason }: any): Promise<void> {
		try {
			this.waiveInProgress = true;
			let waiveDate = DateFormatting.formatDateForJpi(new Date());
			if (this.isApplicationDocumentRequest) {
				await this.directStore.dispatch.ApplicationDocumentRequest.patchApplicationDocumentRequest({
					id: this.documentRequest.id!,
					documentRequestProperties: {
						waivedByLoanOfficerId: this.directStore.state.User.proxyUser?.id,
						dateWaived: waiveDate,
						waivedReason: reason
					}
				});
			} else {
				(this.documentRequest as OnlinePortalDocumentRequest).request.waivedByLoanOfficerId = this.directStore.state.User.proxyUser?.id;
				(this.documentRequest as OnlinePortalDocumentRequest).request.dateWaived = waiveDate;
				(this.documentRequest as OnlinePortalDocumentRequest).request.waivedReason = reason;
				await this.directStore.dispatch.DocumentRequest.updateDocumentRequest(this.documentRequest as OnlinePortalDocumentRequest);
				await this.directStore.dispatch.DocumentRequest.loadPagedDocumentRequests({ page: 1, perPage: 20, refreshCache: true });
			}
			this.waiveInProgress = false;
			return Promise.resolve();
		} catch (err) {
			this.waiveInProgress = false;
			return Promise.reject(err);
		}
	}

	public async deleteAdditionalRequest(): Promise<void> {
		try {
			this.deleteInProgress = true;
			await this.directStore.dispatch.ApplicationDocumentRequest.deleteApplicationDocumentRequest(
				this.documentRequest as OnlinePortalApplicationDocumentRequest
			);
		} finally {
			this.deleteInProgress = false;
		}
	}

	public async reopenDocumentRequest(): Promise<void> {
		try {
			this.reopenInProgress = true;
			if (this.isAdditionalDocument) {
				await this.directStore.dispatch.ApplicationDocumentRequest.deleteApplicationDocumentRequest(
					this.documentRequest as OnlinePortalApplicationDocumentRequest
				);
			} else if (this.isApplicationDocumentRequest) {
				await this.directStore.dispatch.ApplicationDocumentRequest.reOpenApplicationDocumentRequest(
					this.documentRequest as OnlinePortalApplicationDocumentRequest
				);
			} else {
				await this.directStore.dispatch.DocumentRequest.reopenDocumentRequest(this.documentRequest as OnlinePortalDocumentRequest);
			}
			this.reopenInProgress = false;
			this.$bvModal.hide('reopenModal' + this.documentRequest.id);

			return Promise.resolve();
		} catch (err) {
			this.reopenInProgress = false;
			return Promise.reject(err);
		}
	}
}
